/**
 * BRIEF PEDAGÓGICO V4
 * ===================
 * Dispositivo de entrenamiento ejecutivo que prepara cognitivamente al usuario
 * antes de responder, permitiéndole reconocer:
 * 1. Quién evalúa
 * 2. Desde qué poder
 * 3. Qué dimensión se evalúa
 * 4. Qué se exige responder
 * 
 * El brief NO es solo narrativo, sino un DISPOSITIVO DE ENTRENAMIENTO EJECUTIVO.
 */

import React, { useState } from 'react';
import { Target } from 'lucide-react';

/**
 * Tooltip simple para mostrar información adicional
 */
const Tooltip = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!content) return children;

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded shadow-lg whitespace-nowrap z-10">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

/**
 * Componente Brief Pedagógico
 * @param {Object} briefing - Objeto con estructura canónica del brief
 */
const BriefPedagogico = ({ briefing }) => {
  if (!briefing) {
    return null;
  }

  // 🎟️ TCK-P0-UX-BRIEF-KICKOFF-DEDUP-UI
  // Regla UX: el BRIEF mostrado en UI NO debe contener preguntas al usuario.
  // Si el texto del escenario incluye un segmento interrogativo, se recorta.
  const stripQuestionsFromBriefText = (text) => {
    if (!text) return '';
    const value = String(text);
    const idx = value.indexOf('¿');
    if (idx >= 0) return value.slice(0, idx).trim();
    const q = value.indexOf('?');
    if (q >= 0) return value.slice(0, q).trim();
    return value.trim();
  };

  const escenarioTexto = stripQuestionsFromBriefText(briefing?.escenario?.texto);

  return (
    <div
      className="w-full bg-white rounded-xl p-4 border border-gray-200 text-gray-900 dark:text-gray-200 text-sm leading-normal"
      aria-label="Contexto del caso"
    >

      {/* Título con triple defensa de contraste */}
      <div className="inline-flex items-center gap-2 mb-3">
        <div className="h-6 w-1 bg-indigo-600 rounded-full"></div>
        <span className="text-xs font-bold uppercase tracking-wide text-gray-900 dark:text-white">
          Contexto del caso
        </span>
      </div>
      
      {/* A. ESCENARIO (Marco) */}
      {escenarioTexto && (
        <p className="mb-2">
          {escenarioTexto}
        </p>
      )}

      {/* B. ACTOR (Autoridad) */}
      {briefing.actor && (
        <p className="mb-2">
          <strong>{briefing.actor.nombre}</strong>
          {briefing.actor.rol && briefing.actor.organizacion && (
            <span>
              {" "}· {briefing.actor.rol} · {briefing.actor.organizacion}
            </span>
          )}
          {briefing.actor.rol && !briefing.actor.organizacion && (
            <span>
              {" "}· {briefing.actor.rol}
            </span>
          )}
          {!briefing.actor.rol && briefing.actor.organizacion && (
            <span>
              {" "}· {briefing.actor.organizacion}
            </span>
          )}
        </p>
      )}

      {/* C. INTENCIÓN (Propósito evaluativo - opcional, sin tooltip) */}
      {briefing.intencion?.dimension && (
        <p className="mb-2 text-xs italic text-gray-500">
          Evalúa: {briefing.intencion.dimension}
        </p>
      )}

    </div>
  );
};

export default BriefPedagogico;